import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, StyleSheet, Text, View } from 'react-native';

import { BgPortraitPng } from '../../../assets';
import type { ArcadeGameType } from '../../../services/arcadeApi';
import { VIP_GAMES } from '../Bonuses/bonusGamesCatalog';
import AnimatedLoader from '../common/AnimatedLoader';

type Props = {
	gameType: ArcadeGameType;
	/** The game reported its scene on screen: fade out, then unmount. */
	done: boolean;
};

const FADE_OUT_MS = 380;
const GLOW_PULSE_MS = 1800;

/**
 * What covers the arcade WebView from the tap on a card until the game's first scene is
 * on screen: the hall backdrop under a dark wash with a warm glow in the middle, the
 * three-cards Spine loader (the loading cue itself) and the game's name in gold. Without it
 * the player stared at black while the pack booted and fetched its state.
 */
const ArcadeLoadingOverlay = ({ gameType, done }: Props) => {
	const [mounted, setMounted] = useState(true);
	const opacity = useRef(new Animated.Value(1)).current;
	const glow = useRef(new Animated.Value(0)).current;
	const dots = useRef([
		new Animated.Value(0),
		new Animated.Value(0),
		new Animated.Value(0),
	]).current;
	const title = (VIP_GAMES.find(game => game.arcadeGameType === gameType)?.title ?? 'VIP ARCADE')
		.replace(/\n/g, ' ')
		.toUpperCase();

	useEffect(() => {
		const glowLoop = Animated.loop(
			Animated.sequence([
				Animated.timing(glow, {
					toValue: 1,
					duration: GLOW_PULSE_MS,
					easing: Easing.inOut(Easing.sin),
					useNativeDriver: true,
				}),
				Animated.timing(glow, {
					toValue: 0,
					duration: GLOW_PULSE_MS,
					easing: Easing.inOut(Easing.sin),
					useNativeDriver: true,
				}),
			]),
		);
		glowLoop.start();
		return () => glowLoop.stop();
	}, [glow]);

	useEffect(() => {
		if (!done) return;
		Animated.timing(opacity, {
			toValue: 0,
			duration: FADE_OUT_MS,
			easing: Easing.out(Easing.quad),
			useNativeDriver: true,
		}).start(({ finished }) => {
			if (finished) setMounted(false);
		});
	}, [done, opacity]);

	if (!mounted) return null;

	return (
		<Animated.View pointerEvents={done ? 'none' : 'auto'} style={[styles.overlay, { opacity }]}>
			<Image source={BgPortraitPng} style={styles.backdrop} resizeMode='cover' blurRadius={6} />
			<View style={styles.wash} />
			<Animated.View
				pointerEvents='none'
				style={[
					styles.glow,
					{
						opacity: glow.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] }),
						transform: [
							{ scale: glow.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1.08] }) },
						],
					},
				]}
			/>
			<View style={styles.content}>
				<AnimatedLoader scale={1.25} />
				<Text style={styles.title}>{title}</Text>
			</View>
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	overlay: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: '#050308',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	backdrop: {
		...StyleSheet.absoluteFillObject,
		width: '100%',
		height: '100%',
		opacity: 0.55,
	},
	wash: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: 'rgba(5, 3, 8, 0.62)',
	},
	glow: {
		position: 'absolute',
		width: 420,
		height: 420,
		borderRadius: 210,
		backgroundColor: 'rgba(255, 176, 48, 0.22)',
		shadowColor: '#FFB030',
		shadowOpacity: 0.9,
		shadowRadius: 90,
		shadowOffset: { width: 0, height: 0 },
	},
	content: {
		alignItems: 'center',
		justifyContent: 'center',
		rowGap: 22,
	},
	title: {
		color: '#F8DE8B',
		fontSize: 24,
		fontWeight: '800',
		letterSpacing: 2,
		textAlign: 'center',
		textShadowColor: 'rgba(0, 0, 0, 0.6)',
		textShadowOffset: { width: 0, height: 2 },
		textShadowRadius: 4,
	},
});

export default ArcadeLoadingOverlay;
